import {
  signIn,
  signUp,
  confirmSignUp,
  signOut,
  getCurrentUser,
  resetPassword,
  confirmResetPassword,
  signInWithRedirect,
  fetchAuthSession,
  type AuthSession,
  type SignInInput,
  fetchUserAttributes,
  updateUserAttributes,
  confirmUserAttribute,
} from 'aws-amplify/auth'
import { env } from '@/config/env'
import { MockAuthService } from './mockAuth'
import analytics from './analytics'
import LogRocket from 'logrocket'

export interface AuthUser {
  username: string
  email?: string
  firstName?: string
  lastName?: string
  emailVerified?: boolean
}

export interface AuthResult {
  user: AuthUser
  tokens: {
    accessToken: string
    idToken: string
  }
}

export type FederatedProvider = 'Google'

class AuthService {
  private mockAuth: MockAuthService | null = null
  private trackingOptedIn: boolean = false

  constructor() {
    if (env.bypassAuth) {
      console.log('🔓 Using mock auth service')
      this.mockAuth = new MockAuthService()
    }
  }

  get isTrackingOptedIn(): boolean {
    return this.trackingOptedIn
  }
  set isTrackingOptedIn(value: boolean) {
    this.trackingOptedIn = value
  }

  async getUserGroups(): Promise<string[]> {
    if (this.mockAuth) {
      return ['av-pulse-user-group']
    }

    const session = await fetchAuthSession()
    return (
      (session?.tokens?.accessToken?.payload['cognito:groups'] as string[]) ||
      []
    )
  }

  async isInternalUser() {
    if (this.mockAuth) {
      return true
    }

    const groups = await this.getUserGroups()
    return groups.includes('av-pulse-user-group')
  }

  // Username/Password Authentication
  async signIn(username: string, password: string): Promise<AuthResult> {
    if (this.mockAuth) {
      return this.mockAuth.signIn()
    }

    const signInInput: SignInInput = {
      username,
      password,
      options: {
        authFlowType: 'USER_PASSWORD_AUTH',
      },
    }

    const result = await signIn(signInInput)

    if (!result.isSignedIn) {
      throw new Error('Sign in failed')
    }

    // Wait a bit for tokens to be available
    await new Promise(resolve => setTimeout(resolve, 500))

    const session = await this.getSession()
    const user = await this.getCurrentUser()

    if (!session.tokens?.accessToken || !session.tokens?.idToken) {
      throw new Error('Required tokens not available after sign-in')
    }

    if (!(await this.isInternalUser())) {
      analytics.identify(user.username, {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
      })

      analytics.track('signed_in', {
        $email: user.email,
        $first_name: user.firstName,
        $last_name: user.lastName,
      })
    }

    LogRocket.identify(user.username, {
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
    })

    return {
      user,
      tokens: {
        accessToken: session.tokens.accessToken.toString(),
        idToken: session.tokens.idToken.toString(),
      },
    }
  }

  async signUp(
    username: string,
    password: string,
    email: string,
    firstName: string,
    lastName: string,
  ): Promise<void> {
    if (this.mockAuth) {
      return this.mockAuth.signUp()
    }

    await signUp({
      username,
      password,
      options: {
        userAttributes: {
          email,
          given_name: firstName,
          family_name: lastName,
        },
      },
    })
  }

  async confirmSignUp(username: string, code: string): Promise<void> {
    if (this.mockAuth) {
      return this.mockAuth.confirmSignUp()
    }

    await confirmSignUp({
      username,
      confirmationCode: code,
    })
  }

  // Federated Authentication
  async federatedSignIn(provider: FederatedProvider): Promise<void> {
    if (this.mockAuth) {
      return this.mockAuth.federatedSignIn()
    }

    await signInWithRedirect({ provider: provider as 'Google' })
  }

  // User Management
  async getCurrentUser(): Promise<AuthUser> {
    if (this.mockAuth) {
      return this.mockAuth.getCurrentUser()
    }

    const { username } = await getCurrentUser()
    const userAttributes = await fetchUserAttributes()

    LogRocket.identify(username, {
      email: userAttributes.email || '',
      firstName: userAttributes.given_name || '',
      lastName: userAttributes.family_name || '',
    })

    return {
      username,
      email: userAttributes.email,
      firstName: userAttributes.given_name,
      lastName: userAttributes.family_name,
      emailVerified: userAttributes.email_verified === 'true',
    }
  }

  async signOut(): Promise<void> {
    if (this.mockAuth) {
      return this.mockAuth.signOut()
    }
    const user = await this.getCurrentUser()
    await signOut()

    analytics.track('signed_out', {
      $email: user.email,
      $first_name: user.firstName,
      $last_name: user.lastName,
    })

    // stop tracking user activity once they sign out
    analytics.optOut()
    analytics.reset()
    this.trackingOptedIn = false
  }

  // Password Reset
  async resetPassword(username: string): Promise<void> {
    if (this.mockAuth) {
      return this.mockAuth.resetPassword()
    }

    await resetPassword({ username })
  }

  async confirmResetPassword(
    username: string,
    code: string,
    newPassword: string,
  ): Promise<void> {
    if (this.mockAuth) {
      return this.mockAuth.confirmResetPassword()
    }

    await confirmResetPassword({
      username,
      confirmationCode: code,
      newPassword,
    })
  }

  // Utility Methods
  async getSession(): Promise<AuthSession> {
    return fetchAuthSession()
  }

  async isAuthenticated(): Promise<boolean> {
    if (this.mockAuth) {
      return this.mockAuth.isAuthenticated()
    }

    try {
      const session = await fetchAuthSession()
      return !!session.tokens?.accessToken
    } catch (error) {
      console.error('Error checking authentication status:', error)
      return false
    }
  }

  async getAccessToken(): Promise<string | null> {
    if (this.mockAuth) {
      return this.mockAuth.getAccessToken()
    }

    try {
      const session = await fetchAuthSession()
      return session.tokens?.accessToken?.toString() || null
    } catch (error) {
      console.error('Error getting access token:', error)
      return null
    }
  }

  // Email Verification
  async sendVerificationCode(): Promise<void> {
    if (this.mockAuth) {
      return
    }

    await updateUserAttributes({
      userAttributes: {
        email: (await fetchUserAttributes()).email,
      },
    })
  }

  async verifyEmail(code: string): Promise<void> {
    if (this.mockAuth) {
      return
    }

    await confirmUserAttribute({
      userAttributeKey: 'email',
      confirmationCode: code,
    })
  }

  // Mock OAuth methods (only available when using mock auth)
  isMockOAuthCallback(): boolean {
    return this.mockAuth?.isMockOAuthCallback() || false
  }

  completeMockOAuthCallback(): void {
    this.mockAuth?.completeMockOAuthCallback()
  }
}

export const authService = new AuthService()
